import moment from "moment";
import React, { useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "react-query";
import Select from "react-select";
import { toast } from "react-toastify";
import { CrossIcon } from "../../../Shared/Icons";
import { styles } from "../../../Utils/Common";
import { formatForInputDate, getApiData, toNumberOrNull } from "./helpers";
import {
  oncologySupplyQueryKeys,
  useOncologyFacilitiesLookup,
  useOncologyFacilityDetail,
  useOncologyStatesLookup,
} from "./hooks";
import { LookupOption, SupplyOrderItem } from "./types";
import useLang from "Shared/hooks/useLanguage";
import SupplyManagmentService from "Services/SupplyManagement/SupplyManagmentService";

const itemTypes = [
  { value: "Testing Supplies", label: "Testing Supplies" },
  { value: "Shipping Supplies", label: "Shipping Supplies" },
];

const initialShippingInfo = {
  dateofRequest: "",
  representativeName: "",
  representativePhone: "",
  shippingAddress: "",
  city: "",
  stateId: 0,
  zipCode: "",
  phoneNo: "",
  facilityId: 0,
  kitFullName: "",
  kitShippingAddress: "",
  kitCity: "",
  kitState: "",
  kitZipCode: "",
  kitPhone: "",
};

const initialSupplyOrder: SupplyOrderItem = {
  id: 0,
  itemDescription: "",
  supplyOrderId: 0,
  itemType: "",
  supplyItemId: null,
  itemName: "",
  orderQuantityRequested: "",
  comments: "",
  orderQuantityApproved: 0,
  specialRequest: "",
};

function OrderForm() {
  const { t } = useLang();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [shippingInfo, setShippingInfo] = useState(initialShippingInfo);
  const [supplyOrders, setSupplyOrders] = useState<SupplyOrderItem[]>([
    initialSupplyOrder,
  ]);
  const [itemLookups, setItemLookups] = useState<
    Record<string, LookupOption[]>
  >({});

  const { data: facilities = [] } = useOncologyFacilitiesLookup();
  const { data: states = [] } = useOncologyStatesLookup();
  const selectedFacilityId = Number(shippingInfo.facilityId) || 0;
  const { data: facilityData } = useOncologyFacilityDetail(
    selectedFacilityId || undefined
  );

  const saveOrderMutation = useMutation(SupplyManagmentService.saveOrder, {
    onSuccess: (res: any) => {
      if (res?.data?.httpStatusCode === 200) {
        toast.success(res?.data?.message);
        navigate("/manage-order-oncology");
        return;
      }

      toast.error(res?.data?.message || t("Unable to save order"));
    },
    onError: (error: any) => {
      console.error(error);
      toast.error(error?.response?.data?.message || t("Unable to save order"));
    },
  });

  useEffect(() => {
    if (facilities.length === 1 && !shippingInfo.facilityId) {
      setShippingInfo((prev) => ({
        ...prev,
        facilityId: Number(facilities[0].value),
      }));
    }
  }, [facilities, shippingInfo.facilityId]);

  useEffect(() => {
    if (!facilityData) return;

    setShippingInfo((prev) => ({
      ...prev,
      dateofRequest:
        formatForInputDate(facilityData?.dateofRequest) ||
        moment().format("YYYY-MM-DD"),
      representativeName: facilityData?.representativeName ?? "",
      representativePhone: facilityData?.contactPhone ?? "",
      shippingAddress: facilityData?.address ?? "",
      city: facilityData?.city ?? "",
      stateId: Number(facilityData?.stateId ?? 0),
      zipCode: facilityData?.zipCode ?? "",
      phoneNo: facilityData?.facilityPhone ?? "",
      facilityId: facilityData?.facilityId ?? selectedFacilityId,
    }));
  }, [facilityData, selectedFacilityId]);

  const reset = () => {
    navigate("/manage-order-oncology");
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    const keyCode = e.charCode || e.keyCode;
    const allowedKeyCodes = [8, 9, 37, 38, 39, 40, 46];
    if ((keyCode < 48 || keyCode > 57) && !allowedKeyCodes.includes(keyCode)) {
      e.preventDefault();
    }
  };

  const handleEnterPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const loadItemLookUp = async (itemType: string) => {
    if (!itemType || itemLookups[itemType]) return;

    try {
      const res = await queryClient.fetchQuery(
        oncologySupplyQueryKeys.supplyItems(itemType),
        () => SupplyManagmentService.getSupplyItemsLookup(itemType)
      );
      const lookupData = getApiData(res);
      setItemLookups((prev) => ({
        ...prev,
        [itemType]: Array.isArray(lookupData) ? lookupData : (res?.data ?? []),
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handleFacilityChange = (event: LookupOption) => {
    setShippingInfo((prev) => ({
      ...prev,
      facilityId: Number(event.value),
    }));
  };

  const updateSupplyOrder = (
    index: number,
    patch: Partial<SupplyOrderItem>
  ) => {
    setSupplyOrders((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      )
    );
  };

  const handleItemTypeChange = (event: LookupOption, index: number) => {
    updateSupplyOrder(index, {
      itemType: event.label,
      itemName: "",
      supplyItemId: null,
      itemDescription: "",
    });
    loadItemLookUp(`${event.value}`);
  };

  const handleItemChange = async (event: LookupOption, index: number) => {
    updateSupplyOrder(index, {
      itemName: event.label,
      supplyItemId: Number(event.value),
    });

    try {
      const res = await queryClient.fetchQuery(
        oncologySupplyQueryKeys.itemDescription(event.value),
        () => SupplyManagmentService.getSupplyItemDescriptionById(event.value)
      );
      updateSupplyOrder(index, {
        itemDescription: getApiData(res) ?? "",
      });
    } catch (error) {
      console.error(error);
    }
  };

  const addNewRow = () => {
    setSupplyOrders((prev) => [...prev, { ...initialSupplyOrder }]);
  };

  const removeRow = (index: number) => {
    setSupplyOrders((prev) =>
      prev.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const validate = () => {
    if (!shippingInfo.facilityId) {
      toast.error(t("Please select the facility name."));
      return false;
    }

    const selectedItemIds = supplyOrders
      .map((item) => Number(item.supplyItemId))
      .filter(Boolean);

    if (new Set(selectedItemIds).size !== selectedItemIds.length) {
      toast.error(t("Each supply item can only be added once."));
      return false;
    }

    const hasInvalidRow = supplyOrders.some(
      (item) =>
        !item.itemType ||
        !item.supplyItemId ||
        !toNumberOrNull(item.orderQuantityRequested)
    );

    if (hasInvalidRow) {
      toast.error(t("Please fill all required supply order fields."));
      return false;
    }

    return true;
  };

  const addShippingInfo = () => {
    if (saveOrderMutation.isLoading || !validate()) return;

    saveOrderMutation.mutate({
      ...shippingInfo,
      // Date of Request is the moment the order is placed, sent as UTC.
      dateofRequest: new Date().toISOString(),
      id: 0,
      statusId: 1,
      supplyOrderItems: supplyOrders.map((item) => ({
        ...item,
        orderQuantityRequested: toNumberOrNull(item.orderQuantityRequested),
        orderQuantityApproved: toNumberOrNull(item.orderQuantityApproved) ?? 0,
      })),
    });
  };

  return (
    <div>
      <form onSubmit={(e) => e.preventDefault()} onKeyDown={handleEnterPress}>
        <div id="kt_app_toolbar" className="app-toolbar py-2 pt-lg-3">
          <div
            id="kt_app_toolbar_container"
            className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center"
          >
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <ul className="breadcrumb breadcrumb-separatorless fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="" className="text-muted text-hover-primary">
                    {t("Home")}
                  </a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {t("Supply Management")}
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  {t("Oncology Supply Orders")}
                </li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                id="OncologyManageOrderCancel"
                className="btn btn-secondary btn-sm fw-bold"
                onClick={reset}
                type="button"
              >
                <span>{t("Cancel")}</span>
              </button>
              <button
                id="OncologyManageOrderSave"
                className="btn btn-sm fw-bold btn-primary"
                type="button"
                disabled={saveOrderMutation.isLoading}
                aria-busy={saveOrderMutation.isLoading}
                onClick={addShippingInfo}
              >
                {saveOrderMutation.isLoading ? t("Saving...") : t("Save")}
              </button>
            </div>
          </div>
        </div>
        <div className="app-container container-fluid">
          <div id="OncologyShippingInformation" className="card mb-5">
            <div className="align-items-center card-header minh-42px d-flex justify-content-start justify-content-sm-between gap-1">
              <h4 className="m-1">{t("Shipping Information")}</h4>
            </div>
            <div className="card-body py-2 py-md-3">
              <div className="row">
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500 required">
                      {t("Facility Name")}
                    </label>
                    <Select
                      inputId="OncologyManageOrderFacilityName"
                      menuPortalTarget={document.body}
                      options={facilities}
                      theme={(theme) => styles(theme)}
                      placeholder={t("Facility Name")}
                      name="facilityId"
                      value={facilities.filter(
                        (item) => Number(item.value) === shippingInfo.facilityId
                      )}
                      onChange={(event: any) => handleFacilityChange(event)}
                    />
                  </div>
                </div>
                <ReadOnlyInput
                  id="OncologyManageOrderDateOfRequest"
                  label="Date of Request"
                  value={shippingInfo.dateofRequest}
                />
                <ReadOnlyInput
                  id="OncologyManageOrderRepresentativeName"
                  label="Representative Name"
                  value={shippingInfo.representativeName}
                />
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">
                      {t("Representative Phone Number")}
                    </label>
                    <InputMask
                      id="OncologyManageOrderRepresentativePhoneNumber"
                      mask="(999) 999-9999"
                      value={shippingInfo.representativePhone}
                      type="tel"
                      name="representativePhone"
                      onKeyDown={handleKeyPress}
                      className="form-control bg-transparent"
                      placeholder="(999) 999-9999"
                      inputMode="numeric"
                      disabled
                    />
                  </div>
                </div>
                <ReadOnlyInput
                  id="OncologyManageOrderShippingAddress"
                  label="Shipping Address"
                  value={shippingInfo.shippingAddress}
                />
                <ReadOnlyInput
                  id="OncologyManageOrderCity"
                  label="City"
                  value={shippingInfo.city}
                />
                <ReadOnlyInput
                  id="OncologyManageOrderState"
                  label="State"
                  value={
                    facilityData?.state ??
                    states.find(
                      (state) => Number(state.value) === shippingInfo.stateId
                    )?.label ??
                    ""
                  }
                />
                <ReadOnlyInput
                  id="OncologyManageOrderZipCode"
                  label="Zip Code"
                  value={shippingInfo.zipCode}
                />
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">{t("Phone")}</label>
                    <InputMask
                      id="OncologyManageOrderPhoneNumber"
                      mask="(999) 999-9999"
                      value={shippingInfo.phoneNo}
                      type="tel"
                      name="phoneNo"
                      disabled
                      onKeyDown={handleKeyPress}
                      className="form-control bg-transparent"
                      placeholder="(999) 999-9999"
                      inputMode="numeric"
                    />
                  </div>
                </div>
                <div className="col-12">
                  <div className="border-top pt-3 mt-1 mb-3">
                    <h6 className="mb-1 fw-bold">
                      {t("Kit Shipping Details")}
                    </h6>
                    <span className="text-muted fs-8">
                      {t(
                        "Complete only if shipping details are different from Facility."
                      )}
                    </span>
                  </div>
                </div>
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">{t("Full Name")}</label>
                    <input
                      id="OncologyManageOrderKitFullName"
                      type="text"
                      name="kitFullName"
                      className="form-control bg-transparent"
                      placeholder={t("Full Name")}
                      value={shippingInfo.kitFullName}
                      onChange={(event) =>
                        setShippingInfo((prev) => ({
                          ...prev,
                          kitFullName: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">
                      {t("Shipping Address")}
                    </label>
                    <input
                      id="OncologyManageOrderKitShippingAddress"
                      type="text"
                      name="kitShippingAddress"
                      className="form-control bg-transparent"
                      placeholder={t("Shipping Address")}
                      value={shippingInfo.kitShippingAddress}
                      onChange={(event) =>
                        setShippingInfo((prev) => ({
                          ...prev,
                          kitShippingAddress: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">{t("City")}</label>
                    <input
                      id="OncologyManageOrderKitCity"
                      type="text"
                      name="kitCity"
                      className="form-control bg-transparent"
                      placeholder={t("City")}
                      value={shippingInfo.kitCity}
                      onChange={(event) =>
                        setShippingInfo((prev) => ({
                          ...prev,
                          kitCity: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">{t("State")}</label>
                    <input
                      id="OncologyManageOrderKitState"
                      type="text"
                      name="kitState"
                      className="form-control bg-transparent"
                      placeholder={t("State")}
                      value={shippingInfo.kitState}
                      onChange={(event) =>
                        setShippingInfo((prev) => ({
                          ...prev,
                          kitState: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">{t("Zip Code")}</label>
                    <input
                      id="OncologyManageOrderKitZipCode"
                      type="text"
                      name="kitZipCode"
                      className="form-control bg-transparent"
                      placeholder={t("Zip Code")}
                      value={shippingInfo.kitZipCode}
                      onChange={(event) =>
                        setShippingInfo((prev) => ({
                          ...prev,
                          kitZipCode: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
                <div className="col-xl-3 col-md-4 col-sm-6 col-12">
                  <div className="fv-row mb-4">
                    <label className="mb-2 fw-500">{t("Phone")}</label>
                    <input
                      id="OncologyManageOrderKitPhone"
                      type="text"
                      name="kitPhone"
                      className="form-control bg-transparent"
                      placeholder={t("Phone")}
                      value={shippingInfo.kitPhone}
                      onChange={(event) =>
                        setShippingInfo((prev) => ({
                          ...prev,
                          kitPhone: event.target.value,
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div id="OncologySupplyOrder" className="card mb-5">
            <div className="align-items-center card-header minh-42px d-flex justify-content-start justify-content-sm-between gap-1">
              <h4 className="m-1">{t("Supply Order")}</h4>
            </div>
            <div className="card-body py-2 py-md-3">
              {supplyOrders.map((row, index) => (
                <SupplyOrderRow
                  key={index}
                  index={index}
                  row={row}
                  itemLookups={itemLookups}
                  unavailableSupplyItemIds={supplyOrders
                    .filter((_, itemIndex) => itemIndex !== index)
                    .map((item) => Number(item.supplyItemId))
                    .filter(Boolean)}
                  canRemove={supplyOrders.length > 1}
                  removeRow={removeRow}
                  handleKeyPress={handleKeyPress}
                  handleItemTypeChange={handleItemTypeChange}
                  handleItemChange={handleItemChange}
                  updateSupplyOrder={updateSupplyOrder}
                />
              ))}
              <div>
                <button
                  id="OncologyManageOrderAddRows"
                  className="btn btn-sm fw-bold btn-primary"
                  onClick={addNewRow}
                  type="button"
                >
                  <span>{t("Add New Row")}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

function ReadOnlyInput({
  id,
  label,
  value,
}: {
  id: string;
  label: string;
  value: any;
}) {
  const { t } = useLang();
  return (
    <div className="col-xl-3 col-md-4 col-sm-6 col-12">
      <div className="fv-row mb-4">
        <label className="mb-2 fw-500">{t(label)}</label>
        <input
          id={id}
          type="text"
          className="form-control bg-transparent"
          placeholder={t(label)}
          value={value ?? ""}
          disabled
        />
      </div>
    </div>
  );
}

function SupplyOrderRow({
  index,
  row,
  itemLookups,
  unavailableSupplyItemIds,
  canRemove,
  removeRow,
  handleKeyPress,
  handleItemTypeChange,
  handleItemChange,
  updateSupplyOrder,
}: {
  index: number;
  row: SupplyOrderItem;
  itemLookups: Record<string, LookupOption[]>;
  unavailableSupplyItemIds: number[];
  canRemove: boolean;
  removeRow: (index: number) => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  handleItemTypeChange: (event: LookupOption, index: number) => void;
  handleItemChange: (event: LookupOption, index: number) => void;
  updateSupplyOrder: (index: number, patch: Partial<SupplyOrderItem>) => void;
}) {
  const { t } = useLang();
  const itemOptions = row.itemType ? (itemLookups[row.itemType] ?? []) : [];

  return (
    <div className="position-relative mb-4 border-bottom pb-2">
      {canRemove && (
        <div style={{ position: "absolute", top: 0, right: 2, zIndex: 10 }}>
          <button
            id={`OncologyManageOrderRemoveRow_${index}`}
            className="btn btn-icon btn-sm fw-bold btn-table-cancel btn-danger h-27px w-30px fas-icon-20px"
            onClick={() => removeRow(index)}
            type="button"
          >
            <CrossIcon />
          </button>
        </div>
      )}
      <div className="row">
        <div className="col-xl-2 col-md-3 col-sm-6 col-12">
          <div className="fv-row mb-4">
            <label className="mb-2 fw-500 required">{t("Item Type")}</label>
            <Select
              inputId={`OncologyManageOrderItemType_${index}`}
              menuPortalTarget={document.body}
              options={itemTypes}
              theme={(theme: any) => styles(theme)}
              placeholder={t("Item Type")}
              name="itemType"
              value={itemTypes.filter((item) => item.label === row.itemType)}
              onChange={(event: any) => handleItemTypeChange(event, index)}
            />
          </div>
        </div>
        <div className="col-xl-2 col-md-3 col-sm-6 col-12">
          <div className="fv-row mb-4">
            <label className="required mb-2 fw-500">{t("Item")}</label>
            <Select
              inputId={`OncologyManageOrderItem_${index}`}
              menuPortalTarget={document.body}
              options={itemOptions}
              theme={(theme: any) => styles(theme)}
              placeholder={t("Item")}
              name="itemName"
              value={itemOptions.filter((item) => item.label === row.itemName)}
              isOptionDisabled={(option) =>
                unavailableSupplyItemIds.includes(Number(option.value))
              }
              formatOptionLabel={(option, { context }) =>
                context === "menu" &&
                unavailableSupplyItemIds.includes(Number(option.value))
                  ? `${option.label} (${t("Already added")})`
                  : option.label
              }
              onChange={(event: any) => handleItemChange(event, index)}
            />
          </div>
        </div>
        <div className="col-xl-2 col-md-3 col-sm-6 col-12">
          <div className="fv-row mb-4">
            <label className="required mb-2 fw-500">
              {t("Item Description")}
            </label>
            <input
              id={`OncologyManageOrderItemDescription_${index}`}
              type="text"
              name="itemDescription"
              className="form-control"
              placeholder={t("Item Description")}
              value={row.itemDescription ?? ""}
              disabled
            />
          </div>
        </div>
        <div className="col-xl-2 col-md-3 col-sm-6 col-12">
          <div className="fv-row mb-4">
            <label className="required mb-2 fw-500">
              {t("Order Quantity")}
            </label>
            <input
              id={`OncologyManageOrderOrderQuantity_${index}`}
              type="text"
              name="orderQuantityRequested"
              onChange={(e) =>
                updateSupplyOrder(index, {
                  orderQuantityRequested: e.target.value,
                })
              }
              onKeyDown={handleKeyPress}
              className="form-control bg-transparent"
              placeholder={t("Order Quantity")}
              inputMode="numeric"
              value={row.orderQuantityRequested ?? ""}
            />
          </div>
        </div>
        <div className="col-xl-2 col-md-3 col-sm-6 col-12">
          <div className="fv-row mb-4">
            <label className="mb-2 fw-500" style={{ whiteSpace: "nowrap" }}>
              {t("Comment By Order Booker")}
            </label>
            <input
              id={`OncologyManageOrderOrderBooker_${index}`}
              type="text"
              name="comments"
              onChange={(e) =>
                updateSupplyOrder(index, { comments: e.target.value })
              }
              className="form-control bg-transparent"
              placeholder={t("Comment By Order Booker")}
              value={row.comments ?? ""}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderForm;
